import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';

import { QUERY_KEYS } from 'constants/queryKeys';
import { formatDate, formatVehicleStage } from '@utils';
import {
  AVAILABILITY_STATUSES,
  RESERVATIONS_TYPES,
  SERVICE_POINT_STATUSES,
  SUBSCRIBER_ROLES,
} from '@constants';
import { fleetVehiclesServices, servicePointsServices } from '@services';

import { DEFAULT_LOCATIONS } from 'hooks/vehicles/constants';

const SERVICE_POINTS_QUERY_PAYLOAD = {
  page: 1,
  pageSize: 500,
  type: 0,
  field: null,
  operator: null,
  value: [
    {
      field: 'status',
      operator: 0,
      type: 5,
      value: SERVICE_POINT_STATUSES.active,
    },
  ],
};

const getDataMap = (data = []) =>
  data.reduce((acc, cur) => {
    acc[cur.id || cur.value] = { ...cur, name: cur.name || cur.text };
    return acc;
  }, {});

const normalizedChangelogValues = (value) => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (Array.isArray(value)) return JSON.stringify(value);
  return value.toString();
};

export default function useHistoryItemsChangelog() {
  const servicePointsAllResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_ALL_SERVICE_POINTS_KEY],
    () => servicePointsServices.getServicePoints(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const insurancesResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_INSURANCES_KEY],
    () => fleetVehiclesServices.getInsurances(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const roadsideAssistancesResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_ROAD_SIDE_ASSISTANCE_KEY],
    () =>
      fleetVehiclesServices.getRoadsideAssistances(SERVICE_POINTS_QUERY_PAYLOAD)
  );

  const servicePoints = useMemo(() => {
    const d = servicePointsAllResponse.data;
    return d?.data ?? d ?? [];
  }, [servicePointsAllResponse.data]);

  const insurances = useMemo(() => {
    const d = insurancesResponse.data;
    return d?.data ?? d ?? [];
  }, [insurancesResponse.data]);

  const roadsideAssistances = useMemo(() => {
    const d = roadsideAssistancesResponse.data;
    return d?.data ?? d ?? [];
  }, [roadsideAssistancesResponse.data]);

  const servicePointsMap = useMemo(() => {
    return getDataMap([...DEFAULT_LOCATIONS, ...servicePoints]);
  }, [servicePoints]);

  const insurancesMap = useMemo(() => getDataMap(insurances), [insurances]);

  const roadsideAssistanceMap = useMemo(
    () => getDataMap(roadsideAssistances),
    [roadsideAssistances]
  );

  /**
   * @param {Object} log
   * @param {string} log.fieldName
   * @param {string|number} log.valueFrom
   * @param {string|number} log.valueTo
   */
  const handleChangelogItemFieldName = useCallback(
    (log) => {
      const fieldName = log?.fieldName;
      const valueFrom = log?.valueFrom;
      const valueTo = log?.valueTo;

      if (!fieldName || typeof fieldName !== 'string') return log;

      if (
        fieldName.includes('location') ||
        fieldName.includes('CurrentLocation')
      ) {
        const fromLocation = servicePointsMap[valueFrom];
        const toLocation = servicePointsMap[valueTo];

        return {
          ...log,
          valueFrom: fromLocation ? fromLocation.name : valueFrom,
          valueTo: toLocation ? toLocation.name : valueTo,
        };
      }

      if (fieldName.includes('CurrentStageId')) {
        return {
          ...log,
          valueFrom: formatVehicleStage(valueFrom),
          valueTo: formatVehicleStage(valueTo),
        };
      }

      if (fieldName.includes('InsuranceID')) {
        return {
          ...log,
          valueFrom: insurancesMap[valueFrom]?.name || 'empty',
          valueTo: insurancesMap[valueTo]?.name || 'empty',
        };
      }

      if (fieldName.includes('RoadsideAssistanceID')) {
        return {
          ...log,
          valueFrom: roadsideAssistanceMap[valueFrom]?.name || 'empty',
          valueTo: roadsideAssistanceMap[valueTo]?.name || 'empty',
        };
      }

      if (fieldName.includes('ReservationType')) {
        const fromReservation =
          Object.values(RESERVATIONS_TYPES).find(
            (item) => item.value === valueFrom
          )?.text || 'unknown';

        const toReservation =
          Object.values(RESERVATIONS_TYPES).find(
            (item) => item.value === valueTo
          )?.text || 'unknown';

        return {
          ...log,
          valueFrom: !valueFrom ? 'empty' : fromReservation,
          valueTo: !valueTo ? 'empty' : toReservation,
        };
      }

      if (fieldName.includes('AvailabilityStatus')) {
        const fromStatus =
          Object.values(AVAILABILITY_STATUSES).find(
            (item) => item.value === valueFrom
          )?.text || 'unknown';

        const toStatus =
          Object.values(AVAILABILITY_STATUSES).find(
            (item) => item.value === valueTo
          )?.text || 'unknown';

        return {
          ...log,
          valueFrom: !valueFrom ? 'empty' : fromStatus,
          valueTo: !valueTo ? 'empty' : toStatus,
        };
      }

      if (fieldName.includes('Date')) {
        const fromDate = formatDate(valueFrom);
        const toDate = formatDate(valueTo);

        return {
          ...log,
          valueFrom: !valueFrom ? 'empty' : fromDate,
          valueTo: !valueTo ? 'empty' : toDate,
        };
      }

      if (fieldName.includes('Subscriptions') && fieldName.includes('Role')) {
        const fromSubscriptionRole =
          Object.values(SUBSCRIBER_ROLES).find(
            (item) => item.value === valueFrom
          )?.text || 'unknown';

        const toSubscriptionRole =
          Object.values(SUBSCRIBER_ROLES).find((item) => item.value === valueTo)
            ?.text || 'unknown';

        return {
          ...log,
          valueFrom: fromSubscriptionRole,
          valueTo: toSubscriptionRole,
        };
      }

      return {
        ...log,
        valueFrom: normalizedChangelogValues(log?.valueFrom),
        valueTo: normalizedChangelogValues(log?.valueTo),
      };
    },
    [servicePointsMap, insurancesMap, roadsideAssistanceMap]
  );

  /**
   *
   * @param {string} actor
   * @returns {boolean}
   */
  const isSystemActor = (actor) => {
    if (!actor) return true;

    const normalizedActor = actor.trim().toLowerCase();

    return (
      normalizedActor === 'system' ||
      normalizedActor === 'pipedrive' ||
      normalizedActor.startsWith('internal')
    );
  };

  return {
    isSystemActor,
    handleChangelogItemFieldName,
  };
}
